import React, { useState } from 'react';
import TodoItem from './TodoItem';

// ═══════════════════════════════════════════════════════════════
// SWIMLANE GROUP COMPONENT
// ═══════════════════════════════════════════════════════════════

const SwimlaneGroup = ({
  group,
  statuses,
  allTags,
  allTasks = [],
  lockStates = {},
  onToggleComplete,
  onOpenDetails,
  onOpenAttachments,
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRenameTask,
  onDeleteTask,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onConditionsChange,
  onFocusTask,
  onAddSubtask,
  onDrillIn,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Selection for Fokus mode
  selectedTaskIds = new Set(),
  onToggleSelect,
  // Element visibility
  elementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView = 'todolist',
  customViews = [],
  activeCustomViewId = null,
  // Current campaign ID (for detecting cross-campaign navigation)
  currentCampaignId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Deliverables warning modal
  onSubmitDeliverable,
  onNavigateToParentMission,
  nestedSprints = [],
  allSprints = [],
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Count only top-level items (depth 0) for the header count
  const topLevelCount = group.items.filter(item => (item.depth || 0) === 0).length;
  
  return (
    <div className={`swimlane-group ${isCollapsed ? 'swimlane-group--collapsed' : ''}`}>
      <button 
        className="swimlane-group__header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span 
          className="swimlane-group__color" 
          style={{ backgroundColor: group.color }}
        />
        <span className="swimlane-group__label">{group.label}</span>
        <span className="swimlane-group__count">{topLevelCount}</span>
        <svg 
          className={`swimlane-group__chevron ${isCollapsed ? 'swimlane-group__chevron--collapsed' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      
      {!isCollapsed && (
        <div className="swimlane-group__items">
          {group.items.map((item, index) => {
            // Category-based checking: any "completed" category = checked
            const taskStatus = statuses.find(s => s.id === item.task.status);
            const isChecked = item.isSubtask 
              ? item.task.completed === true
              : taskStatus?.category === 'completed';
            const lockState = lockStates[item.task.id];
            const blockingDeps = lockState?.blockingTasks || [];
            const isLocked = lockState?.isLocked && item.task.status !== 'completed';
            
            // Use item.depth if available (for nested items), otherwise 0
            const itemDepth = item.depth || 0;
            const isNestedItem = itemDepth > 0;
            
            return (
              <TodoItem
                key={`${item.task.id}-${itemDepth}-${index}`}
                task={item.task}
                depth={itemDepth}
                isSubtask={item.isSubtask || false}
                isParent={item.isParent || false}
                nestedSprint={item.nestedSprint}
                hasNestedCampaign={item.hasNestedCampaign}
                blockingDependencies={blockingDeps}
                parentMissionName={lockState?.parentMissionName}
                parentCampaignName={lockState?.parentCampaignName}
                parentMissionId={lockState?.parentMissionId}
                parentSprintId={lockState?.parentSprintId}
                isLocked={isLocked}
                lockReason={lockState?.lockReason}
                statuses={statuses}
                allTags={allTags}
                allTasks={item.sprintTasks || allTasks}
                isChecked={isChecked}
                onToggleComplete={() => onToggleComplete(item.task.id, item.isSubtask || false)}
                onOpenDetails={() => onOpenDetails?.(item.task.id)}
                onOpenAttachments={onOpenAttachments}
                onStatusChange={onStatusChange}
                onPriorityChange={onPriorityChange}
                onDifficultyChange={onDifficultyChange}
                onDueDateChange={onDueDateChange}
                onRename={onRenameTask}
                onDelete={onDeleteTask}
                onArchive={onArchiveTask}
                onUnarchive={onUnarchiveTask}
                onDependencyChange={onDependencyChange}
                onConditionsChange={onConditionsChange}
                onFocusTask={onFocusTask}
                onAddSubtask={onAddSubtask}
                onDrillIn={item.nestedSprint ? () => onDrillIn(item.nestedSprint) : undefined}
                onIconChange={onIconChange}
                onAddStatus={onAddStatus}
                onDeleteStatus={onDeleteStatus}
                onRenameStatus={onRenameStatus}
                onSetStatusAutoSync={onSetStatusAutoSync}
                isSelected={!isNestedItem && selectedTaskIds.has(item.task.id)}
                onToggleSelect={!isNestedItem ? onToggleSelect : undefined}
                elementVisibility={elementVisibility}
                currentView={currentView}
                customViews={customViews}
                activeCustomViewId={activeCustomViewId}
                sourceCampaignId={item.sprintId || null}
                onViewChange={onViewChange}
                onSelectCustomView={onSelectCustomView}
                onGoToTaskInView={onGoToTaskInView}
                onDecisionAnswerChange={onDecisionAnswerChange}
                onDecisionOptionsChange={onDecisionOptionsChange}
                customFields={customFields}
                onCustomFieldValueChange={onCustomFieldValueChange}
                onSubmitDeliverable={onSubmitDeliverable}
                onNavigateToParentMission={onNavigateToParentMission}
                nestedSprints={nestedSprints}
                allSprints={allSprints}
              />
            );
          })}
          {group.items.length === 0 && (
            <div className="swimlane-group__empty">No items</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SwimlaneGroup;
